


import React, { useEffect, useState } from "react";
import {
  Flex,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Input,
  Button,
  Box,
  HStack,
  Td,
  Select,
  VStack,
  Link,
  Avatar,
  Icon,
} from "@chakra-ui/react";
import axios from "axios";
import { FaMars, FaVenus } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { getToken } from "utils/auth";
import config from "config";

function FamilyMembers() {
  const [individuals, setIndividuals] = useState([]);
  const [filteredIndividuals, setFilteredIndividuals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCategory, setSearchCategory] = useState("all");
  const [sortCategory, setSortCategory] = useState("");
  const [sortOrder, setSortOrder] = useState("ascendant");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(0);

  const bgColor = useColorModeValue("gray.50", "gray.800");
  const textColor = useColorModeValue("gray.700", "white");


  const { familyCode } = useParams(); // Get familyCode from route params

  // Fetch family members data
  useEffect(() => {
    
    axios
    .get(`${config.API_URL}/individuals/families/${familyCode}/members`)
    .then((response) => {
        console.log(response.data)
        setData(response.data);
      })
      .catch((error) => console.error("Error fetching family members:", error));
  }, [familyCode]);
  useEffect(() => {
    const fetchIndividuals = async () => {
      try {
        const token = getToken()
        const response = await axios.get(
          `${config.API_URL}/individuals/families/${familyCode}/members`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setIndividuals(response.data);
        setFilteredIndividuals(response.data);
        setTotalPages(Math.ceil(response.data.length / itemsPerPage));
      } catch (error) {
        console.error("Error fetching profile information:", error);
      }
    };

    fetchIndividuals();
  }, []);

  const handleSearch = () => {
    let filteredData = individuals;

    if (searchTerm) {
      filteredData = filteredData.filter((individual) => {
        if (searchCategory === "all") {
          return Object.values(individual)
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        } else {
          return (
            individual[searchCategory]
              ?.toString()
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
        }
      });
    }

    setCurrentPage(0);
    setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
    setFilteredIndividuals(filteredData);
  };

  const handleSort = (category) => {
    const newOrder =
      sortCategory === category && sortOrder === "ascendant"
        ? "descendant"
        : "ascendant";
    setSortCategory(category);
    setSortOrder(newOrder);

    const sortedData = [...filteredIndividuals].sort((a, b) => {
      if (newOrder === "ascendant") {
        return a[category] > b[category] ? 1 : -1;
      } else {
        return a[category] < b[category] ? 1 : -1;
      }
    });

    setFilteredIndividuals(sortedData);
  };

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      if (direction === "next" && prev < totalPages - 1) return prev + 1;
      if (direction === "previous" && prev > 0) return prev - 1;
      return prev;
    });
  };

  const displayedData = filteredIndividuals.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
      <Box
        p={{ base: "14px", sm: "18px", md: "24px" }}
        bg={bgColor}
        borderRadius="20px"
        boxShadow="sm"
        w="100%"
        maxW="100%"
      >
        {/* Search and Filter Section */}
        <VStack
          spacing={4}
          align="stretch"
          mb={4}
          w="100%"
          maxW="100%"
        >
          <Flex direction={{ base: "column", md: "row" }} gap={4}>
            <Input
              placeholder="Search..."
              onChange={(e) => setSearchTerm(e.target.value)}
              flex={{ base: "1", md: "1" }}
              borderRadius="12px"
            />
            <Select
              onChange={(e) => setSearchCategory(e.target.value)}
              value={searchCategory}
              flex={{ base: "1", md: "0.3" }}
              borderRadius="12px"
            >
              <option value="all">All</option>
              <option value="individual_code">Individual Code</option>
              <option value="family_code">Family Code</option>
              <option value="geographical_origin">Geographical Origin</option>
            </Select>
            <Button colorScheme="blue" onClick={handleSearch} flex={{ base: "1", md: "0.2" }} borderRadius="12px">
              Search
            </Button>
          </Flex>
        </VStack>

        {/* Table Section */}
        <Box overflowX="auto" borderRadius="16px" border="1px solid" borderColor="gray.200" _dark={{ borderColor: "whiteAlpha.100" }}>
          <Table variant="simple" color={textColor}>
            <Thead>
            <Tr>
        {[
          { label: "Individual", key: "individual_code" },
          { label: "Status", key: "status" },
          { label: "Family", key: "family_code" },
          { label: "Kinship", key: "kinship" },
          { label: "Sex", key: "sex" },
          { label: "Year of Birth", key: "year_of_birth" },
          { label: "Geographical Origin", key: "geographical_origin" },
        ].map(({ label, key }, idx) => (
                  <Th
            key={idx}
            onClick={() => handleSort(key)}
            cursor="pointer"
          >
            {label}
          </Th>
        ))}
      </Tr>
            </Thead>
            <Tbody>
              {displayedData.map((individual) => (
                <Tr key={individual._id}>
                  <Td>
                    <Link
                      color="blue.500"
                      href={`/txd/individuals/info/${individual.individual_code}`}
                    >
                      {individual.individual_code}
                    </Link>
                  </Td>
                  <Td>{individual.status}</Td>
                  <Td>{individual.family_code}</Td>
                  <Td>{individual.kinship}</Td>
                  <Td>
                    {individual.sex === "M" ? (
                      <Icon as={FaMars} color="blue.500" />
                    ) : (
                      <Icon as={FaVenus} color="pink.500" />
                    )}
                  </Td>
                  <Td>{individual.year_of_birth}</Td>
                  <Td>{individual.geographical_origin}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination Section */}
        <HStack justifyContent="center" mt={4} wrap="wrap">
          <Button
            onClick={() => handlePageChange("previous")}
            disabled={currentPage === 0}
            size="sm"
            borderRadius="10px"
          >
            Previous
          </Button>
          <Text fontSize="sm">
            Page {currentPage + 1} of {totalPages}
          </Text>
          <Button
            onClick={() => handlePageChange("next")}
            disabled={currentPage === totalPages - 1}
            size="sm"
            borderRadius="10px"
          >
            Next
          </Button>
        </HStack>
      </Box>
  );
}

export default FamilyMembers;
